import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { toast } from 'sonner'
import { Modal } from './ui/Modal'
import { Button } from './ui/Button'
import { TextField, FieldLabel } from './ui/Field'
import { api, extractApiError, isPlanLimitError } from '../lib/api'
import { folderListResponse } from '../lib/types'

const ROOT_FOLDER_VALUE = '__root__'

const TYPE_COPY = {
  doc: {
    title: 'Create Doc',
    subtitle: 'Docs live inside a Space and can hold rich text, images and embeds.',
    placeholder: 'e.g. Onboarding Guide',
    success: 'Doc has been created successfully.',
  },
  spreadsheet: {
    title: 'Create Spreadsheet',
    subtitle: 'Spreadsheets live inside a Space and can hold tables of data.',
    placeholder: 'e.g. Q3 Revenue Tracker',
    success: 'Spreadsheet has been created successfully.',
  },
  whiteboard: {
    title: 'Create Whiteboard',
    subtitle: 'Whiteboards live inside a Space — sketch, plan and brainstorm visually.',
    placeholder: 'e.g. Roadmap Planning',
    success: 'Whiteboard has been created successfully.',
  },
}

const docSchema = z.object({
  title: z
    .string({ message: 'Title is required.' })
    .trim()
    .min(1, 'Title is required.')
    .min(2, 'Title must be at least 2 characters.')
    .max(200, 'Title must be at most 200 characters.'),
})

export function DocumentCreateDialog({
  open,
  type,
  defaultSpaceId,
  onClose,
  onCreated,
}) {
  const qc = useQueryClient()
  const spaceId = defaultSpaceId
  const [folderId, setFolderId] = useState(null)
  const copy = TYPE_COPY[type]

  const form = useForm({
    resolver: zodResolver(docSchema),
    defaultValues: { title: '' },
  })

  const foldersQuery = useQuery({
    queryKey: ['folders', spaceId],
    enabled: open && Boolean(spaceId),
    queryFn: async () => {
      if (!spaceId) return { data: [] }
      const res = await api.get(`/spaces/${spaceId}/folders`)
      return folderListResponse.parse(res.data)
    },
  })
  const folders = foldersQuery.data?.data ?? []

  useEffect(() => {
    if (open) {
      setFolderId(null)
      form.reset({ title: '' })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, defaultSpaceId, type])

  const createMutation = useMutation({
    mutationFn: async (values) => {
      if (!spaceId) throw new Error('No Space context.')
      const res = await api.post(`/spaces/${spaceId}/documents`, {
        type,
        title: values.title.trim(),
        folderId,
      })
      return res.data
    },
    onSuccess: (created) => {
      toast.success(copy.success)
      void qc.invalidateQueries({ queryKey: ['documents', spaceId] })
      onClose()
      if (spaceId) onCreated?.(created.id, spaceId)
    },
    onError: (err) => {
      // Plan-cap hit: the global API interceptor already shows the single
      // amber "upgrade your plan" warning, so suppress our own error toast.
      if (isPlanLimitError(err)) return
      const { message, fields } = extractApiError(err)
      if (fields?.title) form.setError('title', { message: fields.title })
      else toast.error(message)
    },
  })

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={copy.title}
      description={copy.subtitle}
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={createMutation.isPending}>
            Cancel
          </Button>
          <Button
            type="submit"
            form="document-create-form"
            loading={createMutation.isPending}
            disabled={!spaceId}
          >
            Create
          </Button>
        </>
      }
    >
      <form
        id="document-create-form"
        onSubmit={form.handleSubmit((v) => createMutation.mutate(v))}
        noValidate
        className="space-y-4"
      >
        <TextField
          label="Title"
          required
          placeholder={copy.placeholder}
          minLength={2}
          maxLength={120}
          error={form.formState.errors.title?.message}
          {...form.register('title')}
        />
        <div>
          <FieldLabel label="Project (Optional)" htmlFor="document-folder" />
          <select
            id="document-folder"
            value={folderId ?? ROOT_FOLDER_VALUE}
            onChange={(e) =>
              setFolderId(e.target.value === ROOT_FOLDER_VALUE ? null : e.target.value)
            }
            disabled={!spaceId || foldersQuery.isLoading}
            className="w-full h-10 px-3 rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-card)] text-[color:var(--color-ink)] outline-none focus:border-[color:var(--color-accent)] cursor-pointer disabled:opacity-60"
          >
            <option value={ROOT_FOLDER_VALUE}>(No project — at Space root)</option>
            {folders.map((f) => (
              <option key={f.id} value={f.id}>
                {f.name}
              </option>
            ))}
          </select>
        </div>
      </form>
    </Modal>
  )
}
